/* eslint-disable no-unused-vars */
/**
 * Hook for subscribing to queue job status updates via Supabase real-time.
 *
 * Used by Prompt Debugger to wait for async job completion without using
 * callbacks (callback_function: null pattern).
 *
 * @module useQueueJob
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { logger } from '@musedrops/shared';
import { waitForJob, QueueJobSnapshot } from '../services/queueJob';
import { supabase } from '../services/supabase';

type QueueJobStatus = QueueJobSnapshot;

interface UseQueueJobOptions {
  /** Timeout in milliseconds (default: 10 minutes for TTS) */
  timeout?: number;
  /** Called when job completes successfully */
  onComplete?: (output: Record<string, unknown>) => void;
  /** Called when job fails */
  onError?: (error: string) => void;
  /** Called on timeout */
  onTimeout?: () => void;
}

interface UseQueueJobResult {
  /** Current job status */
  status: QueueJobStatus | null;
  /** Whether we're waiting for a job */
  isWaiting: boolean;
  /** Any error message */
  error: string | null;
  /** Start watching a job by ID */
  watchJob: (jobId: number) => void;
  /** Stop watching and cleanup */
  stopWatching: () => void;
}

/**
 * Hook to subscribe to queue job status updates.
 *
 * Usage:
 * ```tsx
 * const { watchJob, status, isWaiting, error } = useQueueJob({
 *   onComplete: (output) => setAudioUrl(output.audioUrl),
 *   onError: (err) => notify(err, { type: 'error' }),
 *   timeout: 10 * 60 * 1000 // 10 minutes
 * });
 *
 * // Start a job and watch it
 * const jobId = await promptService.createTTSJob(params);
 * watchJob(jobId);
 * ```
 */
export function useQueueJob(
  options: UseQueueJobOptions = {}
): UseQueueJobResult {
  const {
    timeout = 10 * 60 * 1000, // 10 minutes default
    onComplete,
    onError,
    onTimeout,
  } = options;

  const [status, setStatus] = useState<QueueJobStatus | null>(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const jobIdRef = useRef<number | null>(null);

  /**
   * Cleanup subscription, timeout, and polling
   */
  const cleanup = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
    jobIdRef.current = null;
  }, []);

  /**
   * Stop watching the current job
   */
  const stopWatching = useCallback(() => {
    cleanup();
    setIsWaiting(false);
    setStatus(null);
    setError(null);
  }, [cleanup]);

  // Track job start time for timing (moved up so it's available to handleStatusUpdate)
  const jobStartTimeRef = useRef<number>(0);
  const pollCountRef = useRef<number>(0);

  /**
   * Handle job status update from real-time subscription
   */
  const handleStatusUpdate = useCallback(
    (payload: { new: QueueJobStatus }) => {
      const newStatus = payload.new;

      // Only process updates for our watched job
      if (newStatus.id !== jobIdRef.current) {
        return;
      }

      const elapsedTime = jobStartTimeRef.current
        ? `${((performance.now() - jobStartTimeRef.current) / 1000).toFixed(1)}s`
        : 'N/A';

      logger.info('⏱️ [TIMING] Status update received', {
        jobId: newStatus.id,
        status: newStatus.status,
        hasOutput: !!newStatus.output,
        elapsedTime,
      });

      setStatus(newStatus);

      if (newStatus.status === 'completed') {
        logger.info('⏱️ [TIMING] Job COMPLETED', {
          jobId: newStatus.id,
          totalTime: elapsedTime,
          outputKeys: newStatus.output ? Object.keys(newStatus.output) : [],
        });
        setIsWaiting(false);
        cleanup();
        if (onComplete && newStatus.output) {
          onComplete(newStatus.output);
        }
      } else if (
        newStatus.status === 'failed' ||
        newStatus.status === 'cancelled'
      ) {
        logger.info('⏱️ [TIMING] Job FAILED/CANCELLED', {
          jobId: newStatus.id,
          totalTime: elapsedTime,
          error: newStatus.last_error,
        });
        setIsWaiting(false);
        const errorMessage = newStatus.last_error || `Job ${newStatus.status}`;
        setError(errorMessage);
        cleanup();
        if (onError) {
          onError(errorMessage);
        }
      }
      // For 'pending' and 'processing', just update status and keep waiting
    },
    [cleanup, onComplete, onError]
  );

  /**
   * Fallback polling mechanism when real-time subscription fails.
   * Delegates to the shared waitForJob utility (issue #739); the hook's own
   * setTimeout handles give-up, so polling itself never times out.
   */
  const startPolling = useCallback(
    (jobId: number) => {
      // Don't start polling if we're not waiting or job ID doesn't match
      if (jobIdRef.current !== jobId) {
        return;
      }

      // Abort any existing polling loop
      if (pollAbortRef.current) {
        pollAbortRef.current.abort();
      }
      const controller = new AbortController();
      pollAbortRef.current = controller;

      logger.info('⏱️ [TIMING] Starting polling fallback for job', { jobId });
      pollCountRef.current = 0;

      waitForJob(jobId, {
        pollIntervalMs: 2000, // 2 seconds
        timeoutMs: Infinity, // hook-level timeout handles give-up
        signal: controller.signal,
        onPollError: 'continue',
        onUpdate: job => {
          pollCountRef.current += 1;
          const elapsedSinceJobStart = jobStartTimeRef.current
            ? `${((performance.now() - jobStartTimeRef.current) / 1000).toFixed(1)}s`
            : 'N/A';

          logger.info(`⏱️ [TIMING] Poll #${pollCountRef.current}`, {
            jobId,
            elapsedSinceJobStart,
            status: job.status,
            hasOutput: !!job.output,
          });

          // handleStatusUpdate handles terminal states (cleanup + callbacks)
          handleStatusUpdate({ new: job });
        },
      }).catch(err => {
        logger.error('Polling exception', { err, jobId });
      });
    },
    [handleStatusUpdate]
  );

  /**
   * Start watching a job by ID
   */
  const watchJob = useCallback(
    (jobId: number) => {
      // Cleanup any existing subscription
      cleanup();

      jobIdRef.current = jobId;
      jobStartTimeRef.current = performance.now();
      pollCountRef.current = 0;
      setIsWaiting(true);
      setError(null);
      setStatus({ id: jobId, status: 'pending' });

      logger.info('⏱️ [TIMING] watchJob started', { jobId });

      // Set up timeout
      timeoutRef.current = setTimeout(() => {
        setError('Job timed out');
        setIsWaiting(false);
        cleanup();
        if (onTimeout) {
          onTimeout();
        }
      }, timeout);

      // Subscribe to real-time updates for this job
      const channel = supabase
        .channel(`queue-job-${jobId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'process_queue',
            filter: `id=eq.${jobId}`,
          },
          handleStatusUpdate
        )
        .subscribe(subscriptionStatus => {
          logger.debug('useQueueJob subscription status', {
            subscriptionStatus,
            jobId,
          });
          if (subscriptionStatus === 'SUBSCRIBED') {
            // Poll as the source of truth. Realtime `postgres_changes` is
            // unreliable on the local Supabase stack — the Realtime server image
            // lags the realtime-js client, so completion UPDATEs are silently
            // never delivered even though the channel reports SUBSCRIBED, leaving
            // callers stuck until the give-up timeout. This completes the #739
            // polling fallback, which previously only engaged on CLOSED/
            // CHANNEL_ERROR and so never caught the "subscribed but silent" case.
            // Polling's first read also covers the "already completed before we
            // subscribed" race that the old one-shot fetch handled; realtime
            // UPDATE events still short-circuit via handleStatusUpdate if they do
            // arrive (e.g. against hosted Realtime), and cleanup() stops polling
            // the moment the job reaches a terminal state.
            startPolling(jobId);
          } else if (
            subscriptionStatus === 'CLOSED' ||
            subscriptionStatus === 'CHANNEL_ERROR'
          ) {
            // Subscription closed unexpectedly - ensure polling is running.
            logger.warn(
              'useQueueJob subscription closed, falling back to polling',
              { subscriptionStatus, jobId }
            );
            startPolling(jobId);
          }
        });

      channelRef.current = channel;
    },
    [cleanup, handleStatusUpdate, onTimeout, startPolling, timeout]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    status,
    isWaiting,
    error,
    watchJob,
    stopWatching,
  };
}
